import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Alert,
  IconButton,
  Paper,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Close as CloseIcon, CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useFamily, useAuth } from '../../context';
import { useMutation, useQuery } from '@apollo/client/react';
import { GET_FAMILY, UPDATE_FAMILY } from '@common/graphql';
import {
  CustodyState,
  CustodyPlan,
  CustodyPlanWithId,
  CustodyDate,
  Household,
  WeekdayName,
  WeekSplit,
  HouseholdAssignment,
  CustodyScheme,
  CUSTODY_SCHEMES,
  WEEKDAYS,
  DEFAULT_HOUSEHOLD_COLORS,
  SwapRequest,
} from '../../types';
import {
  getCustodyDates,
  getInitialCustodyState,
  custodyPlanToState,
  stateToCustodyPlan,
  getWeekendDayCodes,
} from '../../utils/custody';

interface CustodyFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (plan: CustodyPlan, households: Record<string, Household>) => Promise<void>;
  existingPlan?: CustodyPlanWithId | null;
  households: Record<string, Household>;
  isNewPlan: boolean;
  activePlanId?: string;
}

export const CustodyFormDialog: React.FC<CustodyFormDialogProps> = ({
  open,
  onClose,
  onSave,
  existingPlan,
  households: initialHouseholds,
  isNewPlan,
  activePlanId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { currentFamily: family } = useFamily();

  const [state, setState] = useState<CustodyState>(() => getInitialCustodyState());
  const [saving, setSaving] = useState(false);
  const [households, setHouseholds] = useState<Record<string, Household>>(initialHouseholds);

  // Initialize state when dialog opens
  useEffect(() => {
    if (open) {
      if (existingPlan && !isNewPlan) {
        const restoredState = custodyPlanToState(existingPlan.plan);
        setState({ ...getInitialCustodyState(), ...restoredState });
        setHouseholds(existingPlan.households);
      } else {
        // New plan - set start date to tomorrow
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);

        const oneYearLater = new Date(tomorrow);
        oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);

        setState({
          ...getInitialCustodyState(),
          startDate: tomorrow,
          endDate: oneYearLater,
        });

        // Set up default households if none exist
        if (Object.keys(initialHouseholds).length === 0) {
          setHouseholds({
            hh1: { id: 'hh1', name: 'Household 1', color: DEFAULT_HOUSEHOLD_COLORS[0] },
            hh2: { id: 'hh2', name: 'Household 2', color: DEFAULT_HOUSEHOLD_COLORS[1] },
          });
        } else {
          setHouseholds(initialHouseholds);
        }
      }
    }
  }, [open, existingPlan, isNewPlan, initialHouseholds]);

  // Calculate custody dates when state changes
  const custodyResult = useMemo(() => {
    if (state.custodyScheme && state.startHousehold) {
      return getCustodyDates(state, households);
    }
    return { items: {}, isParametersComplete: false, description: '' };
  }, [state, households]);

  // Get household options
  const householdOptions = useMemo(
    () => Object.entries(households).map(([key, hh]) => ({ key, label: hh.name })),
    [households]
  );

  // Determine which options to show based on scheme
  const showWeekendOptions = state.custodyScheme &&
    ['weekends', 'xweekends', 'weekend_weekdays', 'weekend_splitweek'].includes(state.custodyScheme);
  const showSwapDayOfWeek = state.custodyScheme &&
    ['week', 'xweeks'].includes(state.custodyScheme);
  const showXWeeks = state.custodyScheme === 'xweeks';
  const showXDays = state.custodyScheme === 'xdays';
  const showXWeekends = state.custodyScheme === 'xweekends';
  const showSplits = state.custodyScheme &&
    ['weekend_splitweek', 'alternating'].includes(state.custodyScheme);

  const handleSchemeChange = (schemeKey: CustodyScheme) => {
    const scheme = CUSTODY_SCHEMES.find((s) => s.key === schemeKey);
    if (!scheme) return;

    const hhKeys = Object.keys(households);
    let splits: WeekSplit[] = [];

    if (schemeKey === 'alternating') {
      const hh1: HouseholdAssignment = { householdId: hhKeys[0], color: households[hhKeys[0]].color };
      const hh2: HouseholdAssignment = { householdId: hhKeys[1], color: households[hhKeys[1]].color };
      splits = [
        { SU: hh1, MO: hh1, TU: hh1, WE: hh1, TH: hh1, FR: hh1, SA: hh1 },
        { SU: hh2, MO: hh2, TU: hh2, WE: hh2, TH: hh2, FR: hh2, SA: hh2 },
      ];
    } else if (schemeKey === 'weekend_splitweek' && state.startHousehold) {
      const hh: HouseholdAssignment = {
        householdId: state.startHousehold,
        color: households[state.startHousehold].color,
      };
      const weekendDays = getWeekendDayCodes(state.weekendStart, state.weekendEnd);
      const split: WeekSplit = { SU: hh, MO: hh, TU: hh, WE: hh, TH: hh, FR: hh, SA: hh };
      weekendDays.forEach((day) => {
        delete split[day as keyof WeekSplit];
      });
      splits = [split];
    }

    setState((prev) => ({
      ...prev,
      custodyScheme: schemeKey,
      custodySchemeText: scheme.label,
      splits,
      swapDates: {},
    }));
  };

  const handleHouseholdChange = (householdId: string) => {
    const hh = households[householdId];
    if (!hh) return;

    setState((prev) => ({
      ...prev,
      startHousehold: householdId,
      startHouseholdName: hh.name,
      swapDates: {},
    }));
  };

  const handleSplitDayToggle = (dayCode: string, weekIndex: number) => {
    setState((prev) => {
      const newSplits = [...prev.splits];
      const split = { ...newSplits[weekIndex] };

      const hhKeys = Object.keys(households);
      const currentAssignment = split[dayCode as keyof WeekSplit];

      // Toggle between households
      if (!currentAssignment || currentAssignment.householdId === hhKeys[1]) {
        split[dayCode as keyof WeekSplit] = {
          householdId: hhKeys[0],
          color: households[hhKeys[0]].color,
        };
      } else {
        split[dayCode as keyof WeekSplit] = {
          householdId: hhKeys[1],
          color: households[hhKeys[1]].color,
        };
      }

      newSplits[weekIndex] = split;
      return { ...prev, splits: newSplits, swapDates: {} };
    });
  };

  const handleSave = async () => {
    if (!custodyResult.isParametersComplete) {
      enqueueSnackbar('Please complete all required fields', { variant: 'warning' });
      return;
    }

    if (!family?.id || !user?.id) {
      enqueueSnackbar('Missing family or user information', { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      const plan = stateToCustodyPlan(state, family.id, user.id);
      await onSave(plan, households);
      onClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {existingPlan && !isNewPlan ? 'Edit Custody Schedule' : 'Create Custody Schedule'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Schedule Type */}
          <Grid size={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Schedule Type
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Custody Pattern</InputLabel>
              <Select
                value={state.custodyScheme || ''}
                label="Custody Pattern"
                onChange={(e) => handleSchemeChange(e.target.value as CustodyScheme)}
              >
                {CUSTODY_SCHEMES.map((scheme) => (
                  <MenuItem key={scheme.key} value={scheme.key}>
                    <Box>
                      <Typography>{scheme.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {scheme.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Date Range */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePicker
              label="Start Date"
              value={state.startDate}
              onChange={(date) =>
                date && setState((prev) => ({ ...prev, startDate: date, swapDates: {} }))
              }
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePicker
              label="End Date"
              value={state.endDate}
              onChange={(date) =>
                date && setState((prev) => ({ ...prev, endDate: date, swapDates: {} }))
              }
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>

          {/* Starting/Weekend Household */}
          {state.custodyScheme && state.custodyScheme !== 'alternating' && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>
                  {showWeekendOptions ? 'Weekend Household' : 'Starting Household'}
                </InputLabel>
                <Select
                  value={state.startHousehold || ''}
                  label={showWeekendOptions ? 'Weekend Household' : 'Starting Household'}
                  onChange={(e) => handleHouseholdChange(e.target.value)}
                >
                  {householdOptions.map((opt) => (
                    <MenuItem key={opt.key} value={opt.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: 0.5,
                            bgcolor: households[opt.key]?.color,
                          }}
                        />
                        {opt.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Swap Day of Week */}
          {showSwapDayOfWeek && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Swap Day</InputLabel>
                <Select
                  value={state.swapDayOfWeek}
                  label="Swap Day"
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      swapDayOfWeek: e.target.value as WeekdayName,
                      swapDates: {},
                    }))
                  }
                >
                  {WEEKDAYS.map((day) => (
                    <MenuItem key={day.key} value={day.key}>
                      {day.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Weekend Start/End */}
          {showWeekendOptions && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Weekend Starts</InputLabel>
                  <Select
                    value={state.weekendStart}
                    label="Weekend Starts"
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        weekendStart: e.target.value as WeekdayName,
                        swapDates: {},
                      }))
                    }
                  >
                    {WEEKDAYS.map((day) => (
                      <MenuItem key={day.key} value={day.key}>
                        {day.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Weekend Ends</InputLabel>
                  <Select
                    value={state.weekendEnd}
                    label="Weekend Ends"
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        weekendEnd: e.target.value as WeekdayName,
                        swapDates: {},
                      }))
                    }
                  >
                    {WEEKDAYS.map((day) => (
                      <MenuItem key={day.key} value={day.key}>
                        {day.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {/* Interval Selectors */}
          {showXDays && (
            <Grid size={12}>
              <TextField
                fullWidth
                type="number"
                label="Days Per Household"
                value={state.xDays}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    xDays: parseInt(e.target.value) || 1,
                    swapDates: {},
                  }))
                }
                inputProps={{ min: 1, max: 30 }}
              />
            </Grid>
          )}
          {showXWeeks && (
            <Grid size={12}>
              <TextField
                fullWidth
                type="number"
                label="Weeks Per Household"
                value={state.xWeeks}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    xWeeks: parseInt(e.target.value) || 1,
                    swapDates: {},
                  }))
                }
                inputProps={{ min: 1, max: 12 }}
              />
            </Grid>
          )}
          {showXWeekends && (
            <Grid size={12}>
              <TextField
                fullWidth
                type="number"
                label="Weekends Per Household"
                value={state.xWeekends}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    xWeekends: parseInt(e.target.value) || 1,
                    swapDates: {},
                  }))
                }
                inputProps={{ min: 1, max: 12 }}
              />
            </Grid>
          )}

          {/* Split Week Configuration */}
          {showSplits && state.splits.length > 0 && (
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Weekly Split - Tap days to assign
              </Typography>

              {/* Household Legend */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {Object.values(households).map((hh) => (
                  <Box key={hh.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: 0.5,
                        bgcolor: hh.color,
                      }}
                    />
                    <Typography variant="caption">{hh.name}</Typography>
                  </Box>
                ))}
              </Box>

              {state.splits.map((split, weekIndex) => {
                const weekendDays =
                  state.custodyScheme === 'weekend_splitweek'
                    ? getWeekendDayCodes(state.weekendStart, state.weekendEnd)
                    : [];

                return (
                  <Paper key={weekIndex} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    {state.custodyScheme === 'alternating' && (
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Week {weekIndex + 1}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {WEEKDAYS.map((day) => {
                        const isWeekend = weekendDays.includes(day.short);
                        const assignment = split[day.short as keyof WeekSplit];

                        if (isWeekend && state.custodyScheme === 'weekend_splitweek') {
                          return null; // Hide weekend days for split week
                        }

                        return (
                          <Chip
                            key={day.short}
                            label={day.short}
                            onClick={() => handleSplitDayToggle(day.short, weekIndex)}
                            sx={{
                              bgcolor: assignment?.color || 'grey.300',
                              color: 'white',
                              fontWeight: 600,
                              '&:hover': {
                                opacity: 0.8,
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Paper>
                );
              })}
            </Grid>
          )}

          {/* Description */}
          {custodyResult.description && (
            <Grid size={12}>
              <Alert
                severity={custodyResult.isParametersComplete ? 'info' : 'warning'}
                icon={<CalendarIcon />}
              >
                {custodyResult.description}
              </Alert>
            </Grid>
          )}

          {/* Active Plan Warning */}
          {isNewPlan && activePlanId && (
            <Grid size={12}>
              <Alert severity="warning">
                Creating a new plan will end the current active plan. The current plan will be
                closed on the day before your new plan starts.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !custodyResult.isParametersComplete}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Saving...' : existingPlan && !isNewPlan ? 'Update Plan' : 'Create Plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Swap Request Dialog
interface CustodySwapRequestDialogProps {
  open: boolean;
  onClose: () => void;
  custodyDates: Record<string, CustodyDate>;
  households: Record<string, Household>;
  mode?: 'create' | 'view';
  swapRequests?: SwapRequest[];
}

export const CustodySwapRequestDialog: React.FC<CustodySwapRequestDialogProps> = ({
  open,
  onClose,
  custodyDates,
  households,
  mode = 'create',
  swapRequests = [],
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { currentFamily: family } = useFamily();

  const { data: familyData } = useQuery(GET_FAMILY);
  const [updateFamily] = useMutation(UPDATE_FAMILY);

  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedDates([]);
      setReason('');
    }
  }, [open]);

  // Get available dates (next 30 days)
  const availableDates = useMemo(() => {
    const dates: Array<{ date: string; custodyDate: CustodyDate }> = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];

      if (custodyDates[dateStr]) {
        dates.push({ date: dateStr, custodyDate: custodyDates[dateStr] });
      }
    }

    return dates;
  }, [custodyDates]);

  const toggleDate = (dateStr: string) => {
    setSelectedDates((prev) => {
      if (prev.includes(dateStr)) {
        return prev.filter((d) => d !== dateStr);
      }
      return [...prev, dateStr].sort();
    });
  };

  const handleSubmit = async () => {
    if (selectedDates.length === 0) {
      enqueueSnackbar('Please select at least one date', { variant: 'warning' });
      return;
    }

    if (!family?.id || !user?.id) {
      enqueueSnackbar('Missing family or user information', { variant: 'error' });
      return;
    }

    setSubmitting(true);

    try {
      const firstDate = selectedDates[0];
      const fromHouseholdId = custodyDates[firstDate]?.householdId;
      const hhKeys = Object.keys(households);
      const toHouseholdId = hhKeys.find((k) => k !== fromHouseholdId) || hhKeys[0];

      const swapRequest = {
        id: `swap_${Date.now()}`,
        requestedBy: user.id,
        requestedByName: user.name || 'Parent',
        requestedAt: Date.now(),
        dates: selectedDates,
        fromHouseholdId,
        toHouseholdId,
        reason: reason.trim() || undefined,
        status: 'pending',
      };

      const familyDataObj = (familyData as any)?.family?.[0]?.data || {};
      const existingRequests = familyDataObj.swapRequests || [];

      await updateFamily({
        variables: {
          id: family.id,
          data: {
            ...familyDataObj,
            swapRequests: [...existingRequests, swapRequest],
          },
        },
      });

      enqueueSnackbar('Swap request sent', { variant: 'success' });
      onClose();
    } catch (err) {
      enqueueSnackbar('Failed to send swap request', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const pendingRequests = swapRequests.filter((r) => r.status === 'pending');
  const resolvedRequests = swapRequests.filter((r) => r.status !== 'pending');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{mode === 'view' ? 'Swap Requests' : 'Request Date Swap'}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {mode === 'view' ? (
          <>
            {swapRequests.length === 0 ? (
              <Alert severity="info">No swap requests yet.</Alert>
            ) : (
              <>
                {pendingRequests.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Pending</Typography>
                    {pendingRequests.map((req) => (
                      <Paper key={req.id} variant="outlined" sx={{ p: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {req.requestedByName}
                          </Typography>
                          <Chip label="Pending" size="small" color="warning" />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                          {req.dates.map((d) => {
                            const dateObj = new Date(d + 'T12:00:00');
                            return (
                              <Chip
                                key={d}
                                size="small"
                                label={dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                sx={{ bgcolor: households[req.fromHouseholdId]?.color + '20' }}
                              />
                            );
                          })}
                        </Box>
                        {req.reason && (
                          <Typography variant="caption" color="text.secondary">{req.reason}</Typography>
                        )}
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          {households[req.fromHouseholdId]?.name || 'Household'} → {households[req.toHouseholdId]?.name || 'Household'}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                )}
                {resolvedRequests.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Resolved</Typography>
                    {resolvedRequests.map((req) => (
                      <Paper key={req.id} variant="outlined" sx={{ p: 2, mb: 1, opacity: 0.7 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {req.requestedByName}
                          </Typography>
                          <Chip
                            label={req.status === 'approved' ? 'Approved' : 'Denied'}
                            size="small"
                            color={req.status === 'approved' ? 'success' : 'error'}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {req.dates.map((d) => {
                            const dateObj = new Date(d + 'T12:00:00');
                            return (
                              <Chip
                                key={d}
                                size="small"
                                label={dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                              />
                            );
                          })}
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </>
            )}
          </>
        ) : (
        <>
        <Alert severity="info" sx={{ mb: 3 }}>
          Select the dates you'd like to swap. The other parent will receive a notification to
          approve or deny your request.
        </Alert>

        {/* Household Legend */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {Object.values(households).map((hh) => (
            <Box key={hh.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.5,
                  bgcolor: hh.color,
                }}
              />
              <Typography variant="caption">{hh.name}</Typography>
            </Box>
          ))}
        </Box>

        {/* Date Selection */}
        <Typography variant="subtitle2" gutterBottom>
          Select Dates
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          {availableDates.map(({ date, custodyDate }) => {
            const isSelected = selectedDates.includes(date);
            const dateObj = new Date(date + 'T12:00:00');

            return (
              <Chip
                key={date}
                label={
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" display="block">
                      {dateObj.toLocaleDateString('en-US', { weekday: 'short' })}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {dateObj.getDate()}
                    </Typography>
                  </Box>
                }
                onClick={() => toggleDate(date)}
                variant={isSelected ? 'filled' : 'outlined'}
                sx={{
                  bgcolor: isSelected ? custodyDate.color : 'transparent',
                  borderColor: custodyDate.color,
                  color: isSelected ? 'white' : 'text.primary',
                  height: 56,
                  '& .MuiChip-label': { px: 1.5 },
                }}
              />
            );
          })}
        </Box>

        {/* Selected Dates Summary */}
        {selectedDates.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected ({selectedDates.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {selectedDates.map((date) => {
                const cd = custodyDates[date];
                const dateObj = new Date(date + 'T12:00:00');

                return (
                  <Chip
                    key={date}
                    label={dateObj.toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })}
                    onDelete={() => toggleDate(date)}
                    size="small"
                    sx={{
                      bgcolor: cd?.color + '20',
                      '& .MuiChip-deleteIcon': { color: cd?.color },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {/* Reason */}
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., Work trip, family event..."
        />
        </>
        )}
      </DialogContent>

      {mode === 'create' && (
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || selectedDates.length === 0}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting ? 'Sending...' : 'Send Request'}
          </Button>
        </DialogActions>
      )}
      {mode === 'view' && (
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
